import * as React from "react";
import {
  SimpleForm,
  NumberInput, useNotify,
  TextInput,
  required,
  maxValue
} from "react-admin";
import {
  CircularProgress,
  Alert,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Container,
  Box,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { GdprBanner } from "./GdprBanner";
import { integerOnly } from "../validators";
import { IfNeededAlert } from "./partials/ifNeededAlert";

const COUNT_BINS_MAX = 10000;

const API_URL =
  process.env.REACT_APP_API_URL || "https://aihubcy.hubency.com/api";

enum APPOINTMENT_STATUS_ENUM {
  PENDING = "PENDING",
  CONFIRMED = "CONFIRMED",
  CANCELLED = "CANCELLED",
}

interface AppointmentResponse {
  appointment: {
    _id: string;
    scheduledAt: Date | string;
    status: APPOINTMENT_STATUS_ENUM;
    quantityBin: number;
    quantityBinToBeCollected: number;
    outlet: { name: string };
    provider: { name: string };
  };
}

export const ExternalsOutletCollect: React.FC = () => {
  const { search } = useLocation();
  const token = new URLSearchParams(search).get("inviteToken") || "";
  const notify = useNotify();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [appointment, setAppointment] = React.useState<
    AppointmentResponse["appointment"] | null
  >(null);
  const [showForm, setShowForm] = React.useState(false);
  const [certified, setCertified] = React.useState(false);
  const [thankYouOpen, setThankYouOpen] = React.useState(false);

  React.useEffect(() => {
    if (!token) {
      setError("Lien invalide");
      setLoading(false);
      return;
    }
    fetch(`${API_URL}/appointments/invite?inviteToken=${token}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Échec récupération (${res.status})`);
        return res.json() as Promise<AppointmentResponse>;
      })
      .then((data) => {
        setAppointment(data.appointment);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const onSubmit = async (values: any) => {
    try {
      const res = await fetch(
        `${API_URL}/appointments/${appointment!._id}/collect`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Invite-Token": token,
          },
          body: JSON.stringify(values),
        }
      );
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || res.statusText);
      }
      notify("Données de collecte bien transmises", { type: "info" });
      setShowForm(false);
      setThankYouOpen(true);
    } catch (err: any) {
      notify(`Erreur : ${err.message}`, { type: "error" });
    }
  };

  const initialFormValues = appointment
    ? {
        fullBinsCollectedCount: appointment.quantityBin || 0,
        emptyBinsDepositCount: appointment.quantityBinToBeCollected,
        appointmentId: appointment!._id,
      }
    : {};

  if (loading) return <CircularProgress sx={{ display: "block", m: "auto" }} />;
  if (error)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );

  return (
    <>
      <title>Hubency Carebag - Enregistrer la collecte</title>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography>
          Exutoire&nbsp;: <strong>{appointment!.outlet.name}</strong>
        </Typography>
        <Typography>
          Prestataire&nbsp;: <strong>{appointment!.provider.name}</strong>
        </Typography>
        <Typography gutterBottom>
          Date Prévisionnelle&nbsp;:{" "}
          <strong>
            {new Date(appointment!.scheduledAt).toLocaleDateString("fr-FR", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })}
          </strong>
        </Typography>

        <Divider sx={{ my: 2 }} />
        <Box textAlign="center" sx={{ mt: 3, mb: 3 }}>
          <Button
            variant="outlined"
            color="success"
            onClick={() => setShowForm(true)}
          >
            Enregistrer la collecte
          </Button>
        </Box>
        <IfNeededAlert />
      </Container>

      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setCertified(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Saisie des quantités</DialogTitle>
        <DialogContent dividers>
          <SimpleForm
            onSubmit={onSubmit}
            defaultValues={initialFormValues}
            toolbar={false}
          >
            <NumberInput
              source="fullBinsCollectedCount"
              label="Nombre de caisses pleines récupérées"
              validate={[
                required("La quantité de caisse est requise"),
                maxValue(COUNT_BINS_MAX, `Maximum ${COUNT_BINS_MAX}`),
                integerOnly("Doit être un nombre entier"),
              ]}
            />
            <NumberInput
              source="emptyBinsDepositCount"
              label="Nombre de caisses vides redistribuées"
              validate={[
                required(
                  "La quantité de caisses vides redistribuées est requise"
                ),
                maxValue(COUNT_BINS_MAX, `Maximum ${COUNT_BINS_MAX}`),
                integerOnly("Doit être un nombre entier"),
              ]}
            />
            <TextInput
              source="comment"
              label="Commentaire - observation (optionnel)"
              fullWidth
            />
            <NumberInput
              source="appointmentId"
              defaultValue={appointment!._id}
              style={{ display: "none" }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={certified}
                  onChange={(e) => setCertified(e.target.checked)}
                />
              }
              label="Je certifie que les données insérées sont correctes."
              sx={{ mt: 2 }}
            />
          </SimpleForm>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowForm(false);
              setCertified(false);
            }}
            color="secondary"
            variant="outlined"
          >
            Annuler
          </Button>
          <Button
            onClick={() =>
              document
                .querySelector("form")
                ?.dispatchEvent(
                  new Event("submit", { cancelable: true, bubbles: true })
                )
            }
            color="success"
            variant="contained"
            disabled={!certified}
          >
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={thankYouOpen}
        onClose={() => setThankYouOpen(false)}
        aria-labelledby="thank-you-dialog"
      >
        <DialogTitle id="thank-you-dialog">Merci 🎉</DialogTitle>
        <DialogContent>
          <Typography>
            Vos données de collecte ont bien été transmises.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            L'équipe Hubency vous souhaite une excellente journée 🌿
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThankYouOpen(false)} autoFocus>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <GdprBanner />
    </>
  );
};
